import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Paging through the `page` search param, and the `j` / `k` shortcuts that do
 * the same from the keyboard.
 *
 * Every other search param is carried across, so paging never drops a filter or
 * a sort order.
 */
export default function usePaging(page) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // The first page is the URL with no `page` param, so landing on it drops the
  // param rather than writing `page=1` — one canonical URL for a view's
  // opening page. Anything at or below 1 is not a page and lands there too.
  const goToPage = (next) => {
    const newParams = new URLSearchParams(searchParams);
    if (next > 1) {
      newParams.set('page', String(next));
    } else {
      newParams.delete('page');
    }
    navigate({ search: newParams.toString() });
  };

  const handleNextPage = () => goToPage(page + 1);
  const handlePreviousPage = () => goToPage(page - 1);

  // A `page` the URL can't mean — zero, negative, fractional, unparseable — is
  // stripped rather than paged from, so the address bar agrees with the page
  // actually shown. Replaces rather than pushes: Back should return to wherever
  // the reader came from, not to the address that was just corrected.
  useEffect(() => {
    const raw = searchParams.get('page');
    if (raw === null) return;
    const parsed = Number(raw);
    if (Number.isInteger(parsed) && parsed >= 1) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('page');
    navigate({ search: newParams.toString() }, { replace: true });
  }, [searchParams, navigate]);

  // Re-registered whenever the page or the params change, so the listener never
  // pages from a stale position.
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Only handle if not typing in an input/textarea
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
      }

      if (event.key === 'j') {
        handlePreviousPage();
      } else if (event.key === 'k') {
        handleNextPage();
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [page, searchParams]);

  return { handleNextPage, handlePreviousPage };
}
